import { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Stack, TextField, MenuItem, Button,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Alert, CircularProgress,
} from "@mui/material";
import { listUsers, createUser, updateUser, deleteUser } from "../api";

const ROLES = [
  { value: "reviewer", label: "Reviewer (approve / reject)" },
  { value: "viewer", label: "Viewer (read-only)" },
  { value: "admin", label: "Admin (+ manage users)" },
];

export default function Settings() {
  const [users, setUsers] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("reviewer");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try { setUsers(await listUsers()); } catch { setErr("Could not load users."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setErr(null); setMsg(null); setBusy(true);
    try {
      const u = await createUser({ email, role });
      setMsg(`${u.email} added${u.invite_emailed ? " — invite email sent." : " (couldn't send the invite email — check email config)."}`);
      setEmail(""); setRole("reviewer");
      await load();
    } catch (e2) { setErr(e2?.response?.data?.error || "Could not add user."); }
    finally { setBusy(false); }
  };

  const changeRole = async (u, newRole) => { await updateUser(u.id, { role: newRole }); await load(); };
  const toggleActive = async (u) => { await updateUser(u.id, { active: !u.active }); await load(); };
  const remove = async (u) => {
    if (!confirm(`Remove ${u.email}? They'll lose access.`)) return;
    try { await deleteUser(u.id); await load(); }
    catch (e) { alert(e?.response?.data?.error || "Failed to remove."); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Team &amp; access</Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Invite a teammate</Typography>
          <Box component="form" onSubmit={invite}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="flex-start">
              <TextField label="Work email" type="email" size="small" fullWidth required
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@gofreedompower.com" />
              <TextField label="Role" select size="small" value={role}
                onChange={(e) => setRole(e.target.value)} sx={{ minWidth: 240 }}>
                {ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </TextField>
              <Button type="submit" variant="contained" disabled={busy} sx={{ whiteSpace: "nowrap" }}>Send invite</Button>
            </Stack>
          </Box>
          {msg && <Alert severity="success" sx={{ mt: 2 }}>{msg}</Alert>}
          {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            They'll get an email saying they have access and can sign in with Google using that address.
          </Typography>
        </CardContent>
      </Card>

      {users === null ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <Card>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell><TableCell>Role</TableCell>
                <TableCell>Status</TableCell><TableCell>Last login</TableCell><TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} sx={{ opacity: u.active ? 1 : 0.55 }}>
                  <TableCell>{u.email}{u.name ? ` — ${u.name}` : ""}</TableCell>
                  <TableCell>
                    <TextField select size="small" variant="standard" value={u.role}
                      onChange={(e) => changeRole(u, e.target.value)}>
                      {ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.value}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell>{u.active
                    ? <Chip size="small" color="success" label="active" />
                    : <Chip size="small" label="disabled" />}</TableCell>
                  <TableCell>{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => toggleActive(u)}>{u.active ? "Disable" : "Enable"}</Button>
                    <Button size="small" color="error" onClick={() => remove(u)}>Remove</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </Box>
  );
}
